import * as React from 'react'

/**
 * Inlined from library https://github.com/blackjk3/react-signature-pad
 */

const velocityFilterWeight = 0.7
const minWidth = 0.5
const maxWidth = 2.5
const dotSize = (minWidth + maxWidth) / 2
const penColor = 'black'
const backgroundColor = 'rgba(0,0,0,0)'

interface Point {
    x: number
    y: number
    time: number
}

interface Curve {
    start: Point
    control1: Point
    control2: Point
    end: Point
}

interface RenderProps {
    signaturePad: React.ReactElement<unknown>
    clearSignature: () => void
    getSignature: () => Promise<Blob>
}

interface Props {
    onEnd?: (event: MouseEvent | TouchEvent) => void
    children: (props: RenderProps) => React.ReactElement<unknown>
}

export class SignaturePad extends React.Component<Props, {}> {
    private canvas: HTMLCanvasElement
    private ctx: CanvasRenderingContext2D
    private mouseButtonDown = false
    private points: Point[]
    private lastVelocity: number
    private lastWidth: number
    public isEmpty: boolean

    public componentDidMount() {
        this.ctx = this.canvas.getContext('2d')
        this.clear()
        this.canvas.style.msTouchAction = 'none'
        this.canvas.addEventListener('mousedown', this.onMouseDown)
        this.canvas.addEventListener('mousemove', this.onMouseMove)
        this.canvas.addEventListener('touchstart', this.onTouchStart)
        this.canvas.addEventListener('touchmove', this.onTouchMove)
        document.addEventListener('mouseup', this.onMouseUp)
        document.addEventListener('touchend', this.onTouchEnd)
        window.addEventListener('resize', this.onResize)
        this.onResize()
    }

    public componentWillUnmount() {
        this.canvas.removeEventListener('mousedown', this.onMouseDown)
        this.canvas.removeEventListener('mousemove', this.onMouseMove)
        this.canvas.removeEventListener('touchstart', this.onTouchStart)
        this.canvas.removeEventListener('touchmove', this.onTouchMove)
        document.removeEventListener('mouseup', this.onMouseUp)
        document.removeEventListener('touchend', this.onTouchEnd)
        window.removeEventListener('resize', this.onResize)
    }

    private clear = () => {
        this.ctx.fillStyle = backgroundColor
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
        this.reset()
    }

    private getSignature = () => new Promise<Blob>((resolve) => this.canvas.toBlob(resolve, 'image/png', 0.8))

    private reset() {
        this.points = []
        this.lastVelocity = 0
        this.lastWidth = (minWidth + maxWidth) / 2
        this.isEmpty = true
        this.ctx.fillStyle = penColor
    }

    private onResize = () => {
        // When zoomed out to less than 100%, for some very strange reason,
        // some browsers report devicePixelRatio as less than 1
        // and only part of the canvas is cleared then.
        const ratio = Math.max(window.devicePixelRatio || 1, 1)
        this.canvas.width = this.canvas.offsetWidth * ratio
        this.canvas.height = this.canvas.offsetHeight * ratio
        this.ctx.scale(ratio, ratio)
        this.isEmpty = true
    }

    private onMouseDown = (event: MouseEvent) => {
        if (event.which === 1) {
            this.mouseButtonDown = true
            this.strokeBegin(event)
        }
    }

    private onMouseMove = (event: MouseEvent) => {
        if (this.mouseButtonDown) {
            this.strokeUpdate(event)
        }
    }

    private onMouseUp = (event: MouseEvent) => {
        if (event.which === 1 && this.mouseButtonDown) {
            this.mouseButtonDown = false
            this.strokeEnd(event)
        }
    }

    private onTouchStart = (event: TouchEvent) => {
        const touch = event.changedTouches[0]
        this.strokeBegin(touch)
    }

    private onTouchMove = (event: TouchEvent) => {
        // Prevent scrolling.
        event.preventDefault()

        const touch = event.changedTouches[0]
        this.strokeUpdate(touch)
    }

    private onTouchEnd = (event: TouchEvent) => {
        const wasCanvasTouched = event.target === this.canvas
        if (wasCanvasTouched) {
            this.strokeEnd(event)
        }
    }

    private strokeBegin(event: MouseEvent | Touch) {
        this.reset()
        this.strokeUpdate(event)
    }

    private strokeUpdate(event: MouseEvent | Touch) {
        const point = this.createPoint(event)
        this.addPoint(point)
    }

    private strokeEnd(event: MouseEvent | TouchEvent) {
        const canDrawCurve = this.points.length > 2
        const point = this.points[0]

        if (!canDrawCurve && point) {
            this.strokeDraw(point)
        }
        if (this.props.onEnd != null) {
            this.props.onEnd(event)
        }
    }

    private strokeDraw(point: Point) {
        this.ctx.beginPath()
        this.drawPoint(point.x, point.y, dotSize)
        this.ctx.closePath()
        this.ctx.fill()
    }

    private createPoint(event: MouseEvent | Touch) {
        const rect = this.canvas.getBoundingClientRect()

        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
            time: Date.now(),
        }
    }

    private addPoint(point: Point) {
        this.points.push(point)

        // To reduce the initial lag make it work with 3 points
        // by copying the first point to the beginning.
        if (this.points.length <= 2) return
        if (this.points.length === 3) this.points.unshift(this.points[0])

        const { c2 } = this.calculateCurveControlPoints(this.points[0], this.points[1], this.points[2])
        const { c1: c3 } = this.calculateCurveControlPoints(this.points[1], this.points[2], this.points[3])
        this.addCurve({ start: this.points[1], control1: c2, control2: c3, end: this.points[2] })

        // Remove the first element from the list,
        // so that we always have no more than 4 points in points array.
        this.points.shift()
    }

    private calculateCurveControlPoints(s1: Point, s2: Point, s3: Point) {
        const dx1 = s1.x - s2.x
        const dy1 = s1.y - s2.y
        const dx2 = s2.x - s3.x
        const dy2 = s2.y - s3.y
        const m1 = { x: (s1.x + s2.x) / 2.0, y: (s1.y + s2.y) / 2.0 }
        const m2 = { x: (s2.x + s3.x) / 2.0, y: (s2.y + s3.y) / 2.0 }
        const l1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)
        const l2 = Math.sqrt(dx2 * dx2 + dy2 * dy2)
        const dxm = m1.x - m2.x
        const dym = m1.y - m2.y
        const k = l2 / (l1 + l2)
        const cm = { x: m2.x + dxm * k, y: m2.y + dym * k }
        const tx = s2.x - cm.x
        const ty = s2.y - cm.y

        return {
            c1: { x: m1.x + tx, y: m1.y + ty, time: Date.now() },
            c2: { x: m2.x + tx, y: m2.y + ty, time: Date.now() },
        }
    }

    private addCurve(curve: Curve) {
        let velocity = this.velocityFrom(curve.start, curve.end)
        velocity = velocityFilterWeight * velocity + (1 - velocityFilterWeight) * this.lastVelocity

        const newWidth = this.strokeWidth(velocity)
        this.drawCurve(curve, this.lastWidth, newWidth)

        this.lastVelocity = velocity
        this.lastWidth = newWidth
    }

    private velocityFrom(start: Point, end: Point) {
        return end.time !== start.time ? this.distanceTo(start, end) / (end.time - start.time) : 1
    }

    private distanceTo(start: Point, end: Point) {
        return Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2))
    }

    private drawPoint(x: number, y: number, size: number) {
        this.ctx.moveTo(x, y)
        this.ctx.arc(x, y, size, 0, 2 * Math.PI, false)
        this.isEmpty = false
    }

    private drawCurve(curve: Curve, startWidth: number, endWidth: number) {
        const widthDelta = endWidth - startWidth
        const drawSteps = Math.floor(this.curveLength(curve))

        this.ctx.beginPath()
        for (let i = 0; i < drawSteps; i++) {
            // Calculate the Bezier (x, y) coordinate for this step.
            const t = i / drawSteps
            const tt = t * t
            const ttt = tt * t
            const u = 1 - t
            const uu = u * u
            const uuu = uu * u

            let x = uuu * curve.start.x
            x += 3 * uu * t * curve.control1.x
            x += 3 * u * tt * curve.control2.x
            x += ttt * curve.end.x

            let y = uuu * curve.start.y
            y += 3 * uu * t * curve.control1.y
            y += 3 * u * tt * curve.control2.y
            y += ttt * curve.end.y

            const width = startWidth + ttt * widthDelta
            this.drawPoint(x, y, width)
        }
        this.ctx.closePath()
        this.ctx.fill()
    }

    private curveLength(curve: Curve) {
        const point = (t: number, start: number, c1: number, c2: number, end: number) =>
            start * (1.0 - t) * (1.0 - t) * (1.0 - t) +
            3.0 * c1 * (1.0 - t) * (1.0 - t) * t +
            3.0 * c2 * (1.0 - t) * t * t +
            end * t * t * t

        const steps = 10
        let length = 0
        let px: number
        let py: number

        for (let i = 0; i <= steps; i++) {
            const t = i / steps
            const cx = point(t, curve.start.x, curve.control1.x, curve.control2.x, curve.end.x)
            const cy = point(t, curve.start.y, curve.control1.y, curve.control2.y, curve.end.y)
            if (i > 0) {
                const xdiff = cx - px
                const ydiff = cy - py
                length += Math.sqrt(xdiff * xdiff + ydiff * ydiff)
            }
            px = cx
            py = cy
        }
        return length
    }

    private strokeWidth(velocity: number) {
        return Math.max(maxWidth / (velocity + 1), minWidth)
    }

    public render() {
        return this.props.children({
            signaturePad: (
                <div className="AppCore__ui__SignaturePad">
                    <canvas ref={(ref) => (this.canvas = ref)} />
                </div>
            ),
            clearSignature: this.clear,
            getSignature: this.getSignature,
        })
    }
}
